import {
    Box, Grid, IconButton, InputAdornment, Table, TableBody,
    TableCell, TableContainer, TableHead, TablePagination, TableRow,
    TextField, Tooltip, Typography, Autocomplete
} from '@mui/material'
import React, { useCallback, useEffect, useState } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import SearchIcon from '@mui/icons-material/Search'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import FunctionsIcon from '@mui/icons-material/Functions'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import { useNavigate } from 'react-router-dom'
import { ApiGetTradeFees } from '../../apiService/Core/Apicall'
import PopupDialog from '../../Components/Popups/ConfirmDialog'

interface TradeFee {
    _id: string
    type: 'DIRECT' | 'LOAN' | 'CASHOUT' | 'ADJUSTMENT'
    side: 'BUY' | 'SELL'
    platformFee: number
    algorithmId: number | null
    status: string
    boosterLoanId: string | null
    clientOrderId: string
    userId: string
    currencyPair: string
    amount: number
    loanAmount: number
    quantity: number
    avgPrice: number
    customerPaymentProfileId: string
    customerPaymentProfileType: string
    createdAt: string
    updatedAt: string
    sfoxOrderId?: string
    sfoxStatus?: string | null
    completedAt?: string
    fees: number
    filledAmount: number
    filledQuantity: number
    s3Key?: string
    s3Path?: string
    exchangeProvider?: string
    exchangeStatus?: string
    cryptoName?: string
    cryptoSymbol?: string
    cryptoLogo?: string
}

interface TradeFeesMeta {
    total: number
    page: number
    limit: number
    totalPages: number
}

interface TradeFeesStats {
    totalPlatformFee: number
    totalOrderFee: number
    totalBuyAmount: number
    totalSellAmount: number
    buyCount: number
    sellCount: number
}

const SIDE_OPTIONS = ['BUY', 'SELL']
const TYPE_OPTIONS = ['DIRECT', 'LOAN', 'CASHOUT', 'ADJUSTMENT']

const StatCard: React.FC<{
    label: string
    value: string
    sub?: string
    color: string
    icon: React.ReactNode
}> = ({ label, value, sub, color, icon }) => (
    <Box sx={{
        background: 'linear-gradient(135deg, #2a2a2a 0%, #222 100%)',
        border: '1px solid #333',
        borderRadius: '14px',
        p: 2.5,
        position: 'relative',
        overflow: 'hidden',
        transition: 'border-color 0.25s',
        '&:hover': { borderColor: color + '60' },
        '&::before': {
            content: '""',
            position: 'absolute',
            top: 0, left: 0, right: 0,
            height: '2px',
            background: `linear-gradient(90deg, ${color}00, ${color}, ${color}00)`,
        },
    }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
            <Typography fontSize={10} color="#6f6f6f" textTransform="uppercase" letterSpacing="0.1em">
                {label}
            </Typography>
            <Box sx={{ color, opacity: 0.7 }}>{icon}</Box>
        </Box>
        <Typography fontSize={20} fontWeight={800} color={color} sx={{ letterSpacing: '-0.02em' }}>
            {value}
        </Typography>
        {sub && <Typography fontSize={12} color="#5a5a5a" mt={0.5}>{sub}</Typography>}
    </Box>
)

const TradeFees: React.FC = () => {
    const navigate = useNavigate()

    const [trades, setTrades] = useState<TradeFee[]>([])
    const [meta, setMeta] = useState<TradeFeesMeta | null>(null)
    const [stats, setStats] = useState<TradeFeesStats | null>(null)
    const [search, setSearch] = useState('')
    const [side, setSide] = useState('')
    const [type, setType] = useState('')
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(10)

    const [errorPopup, setErrorPopup] = useState<{ open: boolean; message: string }>({
        open: false, message: ''
    })

    const fetchTrades = useCallback(async () => {
        setTrades([])
        try {
            const params = new URLSearchParams()
            if (side) params.append('side', side)
            if (type) params.append('type', type)
            if (search) params.append('search', search)
            params.append('page', String(page + 1))
            params.append('limit', String(rowsPerPage))

            const res = await ApiGetTradeFees(`?${params.toString()}`)
            if (res?.status) {
                const responseData = res.data
                const dataArray: TradeFee[] = responseData.data
                setTrades(dataArray)
                setMeta({
                    total: responseData.total,
                    page: responseData.page,
                    limit: responseData.limit,
                    totalPages: responseData.totalPages,
                })

                if (res.stats) {
                    setStats(res.stats)
                } else {
                    setStats({
                        totalPlatformFee: dataArray.reduce((a, r) => a + (r.platformFee || 0), 0),
                        totalOrderFee: dataArray.reduce((a, r) => a + (r.fees || 0), 0),
                        totalBuyAmount: dataArray.filter(r => r.side === 'BUY').reduce((a, r) => a + r.amount, 0),
                        totalSellAmount: dataArray.filter(r => r.side === 'SELL').reduce((a, r) => a + r.amount, 0),
                        buyCount: dataArray.filter(r => r.side === 'BUY').length,
                        sellCount: dataArray.filter(r => r.side === 'SELL').length,
                    })
                }
            }
        } catch (error: any) {
            setErrorPopup({
                open: true,
                message: error?.message || error?.response?.data?.message || 'Failed to fetch trade fees',
            })
        }
    }, [side, type, search, page, rowsPerPage])

    useEffect(() => {
        fetchTrades()
    }, [fetchTrades])

    const fmt = (n: number, d = 2) =>
        n?.toLocaleString('en-US', { minimumFractionDigits: d, maximumFractionDigits: d })

    const fmtDate = (s: string) =>
        new Date(s).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' })

    const fmtPrice = (price: number) =>
        price < 1 ? fmt(price, 6) : fmt(price, 2)

    const getSideBadge = (s: string) => {
        const isBuy = s === 'BUY'
        return (
            <Box sx={{
                display: 'inline-block', px: 1.5, py: 0.4, borderRadius: '5px',
                fontSize: '11px', fontWeight: 700,
                color: isBuy ? '#10b981' : '#ef4444',
                backgroundColor: isBuy ? '#10b98120' : '#ef444420',
                border: `1px solid ${isBuy ? '#10b981' : '#ef4444'}`
            }}>
                {s}
            </Box>
        )
    }

    const getTypeBadge = (t: string) => {
        const map: Record<string, { color: string; bg: string; border: string }> = {
            DIRECT: { color: '#3b82f6', bg: '#3b82f620', border: '#3b82f640' },
            LOAN: { color: '#a78bfa', bg: '#a78bfa20', border: '#a78bfa40' },
            CASHOUT: { color: '#f59e0b', bg: '#f59e0b20', border: '#f59e0b40' },
            ADJUSTMENT: { color: '#6b7280', bg: '#6b728020', border: '#6b728040' },
        }
        const s = map[t] || { color: '#9a9a9a', bg: '#9a9a9a20', border: '#9a9a9a40' }
        return (
            <Box sx={{
                display: 'inline-block', px: 1.5, py: 0.4, borderRadius: '5px',
                fontSize: '11px', fontWeight: 700,
                color: s.color, backgroundColor: s.bg, border: `1px solid ${s.border}`
            }}>
                {t}
            </Box>
        )
    }

    return (
        <div className='common-layout flex-column'>
            {/* Header */}
            <Box className="border-10 p-2 px-4 mixed-orange mb-4 both-circle d-flex align-items-center justify-content-between">
                <Box className="d-flex gap-3 align-items-center">
                    <IconButton
                        sx={{ ':hover': { backgroundColor: '#ffffff1A' } }}
                        onClick={() => navigate(-1)}
                    >
                        <ArrowBackIcon sx={{ color: '#ffffff' }} />
                    </IconButton>
                    <Box sx={{
                        width: 30, height: 30, borderRadius: '10px',
                        background: 'rgba(255,255,255,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <SwapHorizIcon sx={{ color: '#fff', fontSize: 20 }} />
                    </Box>
                    <Box>
                        <Typography className='fs-24 text-white fw-medium' sx={{ lineHeight: 1.1 }}>
                            Trade Fees
                        </Typography>
                        <Typography fontSize={11} color="rgba(255,255,255,0.5)" mt={0.2}>
                            All platform trade fee transactions
                        </Typography>
                    </Box>
                </Box>
                <Typography className='fs-24 text-white fw-medium'>
                    Total: {meta?.total ?? '—'} Orders
                </Typography>
            </Box>

            {/* Stat Cards */}
            <Grid container spacing={2} mb={3}>
                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <StatCard
                        label="Total Platform Fees"
                        value={stats ? `$${fmt(stats.totalPlatformFee)}` : '—'}
                        sub="Sum of platformFee (this page)"
                        color="#f2674a"
                        icon={<FunctionsIcon fontSize="small" />}
                    />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 6 }}>
                    <StatCard
                        label="Total Order Fees"
                        value={stats ? `$${fmt(stats.totalOrderFee)}` : '—'}
                        sub="Sum of exchange fees (this page)"
                        color="#a78bfa"
                        icon={<SwapHorizIcon fontSize="small" />}
                    />
                </Grid>
            </Grid>

            <Box className="bg-dark-color border-10 p-4">
                <Box className="d-flex align-items-center gap-3 mb-4" sx={{ flexWrap: 'wrap' }}>
                    <TextField
                        placeholder="Search currency, user..."
                        value={search}
                        onChange={(e) => { setSearch(e.target.value); setPage(0) }}
                        size="small"
                        sx={searchFieldStyle}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon className='color-primary' />
                                </InputAdornment>
                            ),
                        }}
                    />
                    <Autocomplete
                        size="small"
                        value={side}
                        disableClearable={!side}
                        onChange={(_, newValue) => { setSide(newValue || ''); setPage(0) }}
                        options={SIDE_OPTIONS}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Side"
                                sx={autocompleteFieldStyle}
                                InputProps={{
                                    ...params.InputProps,
                                    sx: { color: '#fff', '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: '#fff' } }
                                }}
                            />
                        )}
                        sx={{ width: 140 }}
                        PaperComponent={({ children }) => (
                            <Box className="bg-dark-color" sx={{
                                color: '#fff',
                                '& .MuiAutocomplete-option': {
                                    '&:hover': { bgcolor: '#241815' },
                                    "&[aria-selected='true']": { bgcolor: '#241815' }
                                }
                            }}>
                                {children}
                            </Box>
                        )}
                    />
                    <Autocomplete
                        size="small"
                        value={type}
                        disableClearable={!type}
                        onChange={(_, newValue) => { setType(newValue || ''); setPage(0) }}
                        options={TYPE_OPTIONS}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Type"
                                sx={autocompleteFieldStyle}
                                InputProps={{
                                    ...params.InputProps,
                                    sx: { color: '#fff', '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': { color: '#fff' } }
                                }}
                            />
                        )}
                        sx={{ width: 160 }}
                        PaperComponent={({ children }) => (
                            <Box className="bg-dark-color" sx={{
                                color: '#fff',
                                '& .MuiAutocomplete-option': {
                                    '&:hover': { bgcolor: '#241815' },
                                    "&[aria-selected='true']": { bgcolor: '#241815' }
                                }
                            }}>
                                {children}
                            </Box>
                        )}
                    />
                </Box>
                <TableContainer sx={{ maxHeight: '60vh', overflow: 'auto' }}>
                    <Table stickyHeader sx={{
                        '& .MuiTableCell-root': { borderBottom: 'none' },
                        '& .MuiTableRow-root': { borderBottom: 'none' }
                    }}>
                        <TableHead>
                            <TableRow>
                                {['No', 'Date', 'Asset', 'Type', 'Side', 'Amount', 'Filled Amount', 'Avg Price', 'Order Fee', 'Platform Fee', 'Status', ''].map((head) => (
                                    <TableCell
                                        key={head}
                                        className='color-primary'
                                        sx={{
                                            backgroundColor: '#241815',
                                            color: '#ff6a3d',
                                            position: 'sticky',
                                            top: 0,
                                            zIndex: 2,
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        {head}
                                    </TableCell>
                                ))}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {trades.length > 0 ? trades.map((trade, index) => (
                                <TableRow key={trade._id} sx={{ '&:hover': { backgroundColor: '#222' } }}>
                                    <TableCell className='text-white py-1' sx={{ color: '#555', fontSize: '11px' }}>
                                        {page * rowsPerPage + index + 1}
                                    </TableCell>
                                    <TableCell className='py-1' sx={{ color: '#7a7a7a', fontSize: '11px', whiteSpace: 'nowrap' }}>
                                        {fmtDate(trade.createdAt)}
                                    </TableCell>
                                    <TableCell className='py-1' sx={{ whiteSpace: 'nowrap' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            {trade.cryptoLogo && (
                                                <Box
                                                    component="img"
                                                    src={trade.cryptoLogo}
                                                    alt={trade.cryptoSymbol}
                                                    sx={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0 }}
                                                    onError={(e: any) => { e.target.style.display = 'none' }}
                                                />
                                            )}
                                            <Box>
                                                <Typography fontSize={12} fontWeight={600} color="#e0e0e0" lineHeight={1.2}>
                                                    {trade.cryptoSymbol ?? trade.currencyPair.replace('usd', '').toUpperCase()}
                                                </Typography>
                                                <Typography fontSize={10} color="#5a5a5a" lineHeight={1.2}>
                                                    {trade.currencyPair.toUpperCase()}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </TableCell>
                                    <TableCell className='py-1'>
                                        {getTypeBadge(trade.type)}
                                    </TableCell>
                                    <TableCell className='py-1'>
                                        {getSideBadge(trade.side)}
                                    </TableCell>
                                    <TableCell className='py-1' sx={{ whiteSpace: 'nowrap' }}>
                                        <Typography
                                            fontSize={13}
                                            fontWeight={700}
                                            color={trade.side === 'BUY' ? '#10b981' : '#ef4444'}
                                        >
                                            {trade.side === 'BUY' ? '+' : '-'}${fmt(trade.amount)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell className='py-1' sx={{ color: '#9a9a9a', fontSize: '12px', whiteSpace: 'nowrap' }}>
                                        ${fmt(trade.filledAmount)}
                                    </TableCell>
                                    <TableCell className='py-1' sx={{ fontFamily: 'monospace', fontSize: '11px', color: '#9a9a9a', whiteSpace: 'nowrap' }}>
                                        ${fmtPrice(trade.avgPrice)}
                                    </TableCell>
                                    <TableCell className='py-1' sx={{ whiteSpace: 'nowrap' }}>
                                        <Typography fontSize={12} fontWeight={600} color="#f2674a">
                                            ${fmt(trade.fees, 6)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell className='py-1' sx={{ whiteSpace: 'nowrap' }}>
                                        <Typography fontSize={12} fontWeight={600} color="#a78bfa">
                                            ${fmt(trade.platformFee, 5)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell className='py-1'>
                                        <Box sx={{
                                            display: 'inline-block', px: 1.5, py: 0.4,
                                            borderRadius: '5px', fontSize: '11px', fontWeight: 600,
                                            color: '#10b981', backgroundColor: '#10b98120', border: '1px solid #10b98140'
                                        }}>
                                            {trade.status}
                                        </Box>
                                    </TableCell>
                                    <TableCell className='py-1'>
                                        {trade.s3Path ? (
                                            <Tooltip title="View Trade Confirmation PDF" arrow>
                                                <Box
                                                    component="a"
                                                    href={trade.s3Path}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    sx={{
                                                        display: 'flex', alignItems: 'center', gap: 0.5,
                                                        color: '#f2674a', fontSize: '11px',
                                                        textDecoration: 'none',
                                                        '&:hover': { color: '#e05a3f' }
                                                    }}
                                                >
                                                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                                                    PDF
                                                </Box>
                                            </Tooltip>
                                        ) : (
                                            <Typography fontSize={11} color="#444">—</Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={12} align="center" sx={{ py: 6, color: '#6f6f6f' }}>
                                        <SwapHorizIcon sx={{ fontSize: 40, opacity: 0.2, mb: 1, display: 'block', mx: 'auto' }} />
                                        <Typography color="#6f6f6f" fontSize={14}>No trade fee records found</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>

                <TablePagination
                    className='bg-dark-color'
                    component="div"
                    count={meta?.total ?? 0}
                    page={page}
                    onPageChange={(_, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0) }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    showFirstButton
                    showLastButton
                    sx={paginationStyle}
                />
            </Box>

            <PopupDialog
                open={errorPopup.open}
                onClose={() => setErrorPopup({ open: false, message: '' })}
                onConfirm={() => setErrorPopup({ open: false, message: '' })}
                variant="error"
                title="Something Went Wrong"
                message={errorPopup.message}
                confirmText="Try Again"
                cancelText="Dismiss"
            />
        </div>
    )
}

export default TradeFees

const searchFieldStyle = {
    width: 320, background: 'transparent', borderRadius: '7px',
    border: '1px solid #898989', transition: 'border-color 0.3s',
    '&:hover': { border: '1px solid #ff6a3d' },
    '&:focus-within': { border: '1px solid #ff6a3d' },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& input': { color: '#fff' },
    '& input::placeholder': { color: '#999', opacity: 1 },
}

const autocompleteFieldStyle = {
    '& .MuiInputBase-root': { background: 'transparent', borderRadius: '8px', border: '1px solid #898989', transition: 'border-color 0.3s' },
    '& .MuiInputBase-root:hover': { border: '1px solid #ff6a3d' },
    '& .MuiInputBase-root.Mui-focused': { border: '1px solid #ff6a3d' },
    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
    '& input::placeholder': { color: '#999', opacity: 1 },
}

const paginationStyle = {
    color: '#898989', borderTop: '1px solid #2a2a2a',
    '& .MuiTablePagination-toolbar': { justifyContent: 'center' },
    '& .MuiTablePagination-selectLabel': { color: '#898989', margin: 0 },
    '& .MuiTablePagination-displayedRows': { color: '#898989', margin: 0 },
    '& .MuiSelect-select': { color: '#898989' },
    '& .MuiSelect-icon': { color: '#898989' },
    '& .MuiIconButton-root': { color: '#898989' },
    '& .MuiIconButton-root.Mui-disabled': { color: '#555' },
}